import React, { useCallback, useEffect, useRef } from 'react'
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver'
import {
  BrandLandingTabItemInteraction,
  useContentInteraction,
  HOVER_THRESHOLD_MS,
} from '@/services/Analytics/ContentInteractions'
import { Livestream } from '@/services/LivestreamService'
import { LivestreamThumbnail } from '../LivestreamThumbnail'

export interface LivestreamCardProps extends Livestream {
  itemIndex: number
  pagePlacement: number
}

export const LivestreamCard: React.FC<LivestreamCardProps> = ({ itemIndex, pagePlacement, ...livestream }) => {
  const { trackInteraction } = useContentInteraction()
  const [cardRef, isVisible] = useIntersectionObserver<HTMLDivElement>({
    threshold: 0.9, // Track when at least 90% of the card is visible
  })

  // Hover tracking state
  const hoverStartTimeRef = useRef<number | null>(null)
  const impressionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasTrackedImpression = useRef(false)

  const livestreamId = livestream.guid
  const livestreamThumbnailPath = livestream.bannerCloudinaryPath

  const handleMouseEnter = useCallback(() => {
    if (!livestreamId) return

    // Record the start time
    hoverStartTimeRef.current = Date.now()
  }, [livestreamId])

  const handleMouseLeave = useCallback(() => {
    if (!livestreamId || !hoverStartTimeRef.current) return

    const dwellTime = Date.now() - hoverStartTimeRef.current

    // Only track hover if the user hovered for at least the threshold time
    if (dwellTime >= HOVER_THRESHOLD_MS && livestreamThumbnailPath) {
      const interaction = new BrandLandingTabItemInteraction({
        id: livestreamId,
        __typename: 'ContentLivestream',
        attribution: {
          itemIndex,
          pagePlacement,
          image: {
            cloudinaryPath: livestreamThumbnailPath,
          },
        },
      })
        .withSurface('brand_landing')
        .withPlacement('extras_tab', pagePlacement, itemIndex, 'vertical_list')
        .withImageArtwork(livestreamThumbnailPath, 'landscape_display')
        .withHover(dwellTime)

      trackInteraction(interaction)
    }

    // Reset state
    hoverStartTimeRef.current = null
  }, [livestreamId, livestreamThumbnailPath, pagePlacement, itemIndex, trackInteraction])

  const handleClick = useCallback(() => {
    if (!livestreamId || !livestreamThumbnailPath) return

    const interaction = new BrandLandingTabItemInteraction({
      id: livestreamId,
      __typename: 'ContentLivestream',
      attribution: {
        itemIndex,
        pagePlacement,
        image: {
          cloudinaryPath: livestreamThumbnailPath,
        },
      },
    })
      .withSurface('brand_landing')
      .withPlacement('extras_tab', pagePlacement, itemIndex, 'vertical_list')
      .withImageArtwork(livestreamThumbnailPath, 'landscape_display')
      .withPress()

    trackInteraction(interaction)
  }, [livestreamId, livestreamThumbnailPath, pagePlacement, itemIndex, trackInteraction])

  // Track impression when the card becomes visible for at least 1 second
  useEffect(() => {
    if (!livestreamId || hasTrackedImpression.current) return

    if (isVisible) {
      // Start a timer to track impression after 1 second
      impressionTimeoutRef.current = setTimeout(() => {
        hasTrackedImpression.current = true

        if (livestreamThumbnailPath) {
          const interaction = new BrandLandingTabItemInteraction({
            id: livestreamId,
            __typename: 'ContentLivestream',
            attribution: {
              itemIndex,
              pagePlacement,
              image: {
                cloudinaryPath: livestreamThumbnailPath,
              },
            },
          })
            .withSurface('brand_landing')
            .withPlacement('extras_tab', pagePlacement, itemIndex, 'vertical_list')
            .withImageArtwork(livestreamThumbnailPath, 'landscape_display')
            .withImpression()

          trackInteraction(interaction)
        }
      }, 1000) // Wait 1 second before tracking impression
    } else {
      // Clear timeout if item becomes not visible before 1 second
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
        impressionTimeoutRef.current = null
      }
    }

    return () => {
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
      }
    }
  }, [isVisible, livestreamId, livestreamThumbnailPath, trackInteraction, itemIndex, pagePlacement])

  return (
    <div ref={cardRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onClick={handleClick}>
      <LivestreamThumbnail {...livestream} />
    </div>
  )
}
